// Shared label + date vocabulary used by the program-author surfaces
// (batch-create-modal Schedule preview, batch-progress Program timeline).
// Both render `Day N · weekday, Mon DD · TYPE`, so the canonical
// SHORT_WEEKDAY / SHORT_MONTH arrays and the ITEM_TYPE_LABEL map live
// in one place — drift between the two surfaces is exactly the kind of
// thing we kept hitting before this was extracted.

const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const SHORT_MONTH = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export const ITEM_TYPE_LABEL = {
  session: 'Session',
  custom_question: 'Question',
  topic: 'Town hall',
  poll: 'Poll',
  checklist: 'Checklist',
  reel: 'Reel',
  spark: 'Spark set',
}

export function formatScheduleDate(d) {
  return `${SHORT_WEEKDAY[d.getDay()]}, ${SHORT_MONTH[d.getMonth()]} ${d.getDate()}`
}

export function itemTypeLabel(type) {
  return ITEM_TYPE_LABEL[type] || (type ? String(type).replace(/_/g, ' ') : 'Item')
}

// Display-only +1 shift so participants and authors read "Day 1 = start
// date" while storage stays 0-indexed (offsetDays = days after start,
// matching the FN scheduler's `baseStart + offsetDays` formula).
export function dayLabel(offsetDays) {
  return `Day ${(Number(offsetDays) || 0) + 1}`
}

// 1-indexed counterpart of an `offsetDays` value (for inputs / hints
// where the bare number, not the "Day N" string, is needed).
export function dayNumber(offsetDays) {
  return (Number(offsetDays) || 0) + 1
}

// Local-date YYYY-MM-DD — matches the date math processBatch performs
// (`baseStart + offsetDays` → `moment.utc(...).format('YYYY-MM-DD')`).
export function toIsoDate(d) {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Always evaluate freshly so a long-lived dialog rolls over at midnight.
export function todayIso() {
  return toIsoDate(new Date())
}

// Translate a raw processFailedReason from programsRuntimeProcess.js into
// {friendly, technical}. `friendly` is the plain-English explanation the
// admin sees first; `technical` is the raw code/message they can copy and
// share with support. Reason formats emitted by processBatch:
//   - clone_failed:<itemType>:<message>
//   - deliveries_write_failed:<message>
//   - coarse codes: batch_not_found, batch_missing_fields, program_not_found,
//     audience_expansion_failed, batch_invalid_startDate, batch_update_failed
//   - thrown error: the raw Error.message
export function formatBatchFailure(rawReason) {
  const reason = String(rawReason || '').trim()
  if (!reason) {
    return {
      friendly: "Something went wrong while scheduling this batch.",
      technical: '',
    }
  }

  // clone_failed:<itemType>:<message>
  // e.g. "clone_failed:checklist:townhall/EsMpKa... not found"
  if (reason.indexOf('clone_failed:') === 0) {
    const parts = reason.split(':')
    const itemType = parts[1] || ''
    const msg = parts.slice(2).join(':')
    const typeLabel = (ITEM_TYPE_LABEL[itemType] || itemType || 'Item').toLowerCase()
    if (/not found/i.test(msg)) {
      return {
        friendly: `One of the program's ${typeLabel} items can't be scheduled because its source content has been deleted. Edit the program to remove or replace that item, then retry.`,
        technical: reason,
      }
    }
    return {
      friendly: `One of the program's ${typeLabel} items couldn't be scheduled. The program may need to be edited before this batch can run.`,
      technical: reason,
    }
  }

  // deliveries_write_failed:<message>
  if (reason.indexOf('deliveries_write_failed:') === 0) {
    return {
      friendly: "We couldn't write this batch's deliveries. This is usually a temporary issue — retrying should clear it.",
      technical: reason,
    }
  }

  if (reason.indexOf('reel_unavailable:') === 0) {
    return {
      friendly: "A Reel in this program is no longer published or ready to play. Replace it with a published Reel, then retry the run.",
      technical: reason,
    }
  }

  const COARSE = {
    batch_not_found: "This batch's record couldn't be read. It may have been deleted in another tab.",
    batch_missing_fields: "The batch is missing required information (program, start date, or company).",
    program_not_found: "The program this batch was built from no longer exists. The batch can't be rerun without it.",
    audience_expansion_failed: "We couldn't turn this batch's audience into a participant list. A team, cohort, or person referenced in the audience may have been removed.",
    batch_invalid_startDate: "The batch's start date couldn't be read.",
    batch_update_failed: "The batch couldn't be finalized after processing. Retrying usually clears this.",
    process_failed: "Something went wrong while scheduling this batch.",
  }
  if (COARSE[reason]) {
    return { friendly: COARSE[reason], technical: reason }
  }

  // Unknown / raw thrown-error message.
  return {
    friendly: "Something went wrong while scheduling this batch. Retrying may help.",
    technical: reason,
  }
}
